import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { Fragment, useEffect, useId, useState } from "react";
import { toast } from "sonner";
import {
  api,
  ApiError,
  type NovautFileResult,
  type NovautPlanResult,
  type NovautRun,
  type TriggerNovautRunResponse,
} from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { downloadBlob } from "@/lib/download-blob";
import { formatDuration, formatTimestamp } from "@/lib/job-format";

const POLL_INTERVAL_MS = 5_000;
const DEFAULT_PAGE_SIZE = 10;

// Columns, so the expanded detail row can span all of them.
const COLUMN_COUNT = 6;

const NUMBER = new Intl.NumberFormat("en-GB");

function runLabel(run: Pick<NovautRun, "kind" | "origin">): string {
  if (run.kind === "plan") return "Dry run";
  return run.origin === "night" ? "Nightly" : "Manual";
}

interface StateStyle {
  label: string;
  icon: LucideIcon;
  variant: "default" | "secondary" | "destructive" | "outline" | "success";
  spin?: boolean;
}

// Icon + text on every badge, never colour alone. Amber (the panel accent) is
// worth opening, red is an error.
const STATES: Record<string, StateStyle> = {
  active: { label: "Running", icon: Loader2, variant: "secondary", spin: true },
  waiting: { label: "Queued", icon: Clock, variant: "outline" },
  delayed: { label: "Queued", icon: Clock, variant: "outline" },
  completed: { label: "Done", icon: CheckCircle2, variant: "success" },
  failed: { label: "Failed", icon: XCircle, variant: "destructive" },
};
// A file run that wrote no file; the details say why.
const NO_FILE: StateStyle = { label: "No file", icon: AlertTriangle, variant: "default" };
const NO_FILE_FAILED: StateStyle = { label: "No file", icon: XCircle, variant: "destructive" };
// A dry run cut by a restart: its counters and log cover part of the catalogue.
const STOPPED: StateStyle = { label: "Stopped", icon: AlertTriangle, variant: "default" };

export interface NovautRunsCardProps {
  /** Why the file cannot be written now, or null. */
  fileRunRefusal: string | null;
  pageSize?: number;
}

export function NovautRunsCard({
  fileRunRefusal,
  pageSize = DEFAULT_PAGE_SIZE,
}: NovautRunsCardProps) {
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["novaut-runs", pageSize, offset],
    queryFn: () => api.novautRuns(pageSize, offset),
    refetchInterval: POLL_INTERVAL_MS,
    placeholderData: keepPreviousData,
  });

  const afterTrigger = (what: string) => (result: TriggerNovautRunResponse) => {
    if (result.outcome === "already-running") {
      toast.info(`A Novaut run is already going (job ${result.jobId})`);
    } else {
      toast.success(`${what} queued (job ${result.jobId})`);
    }
    void queryClient.invalidateQueries({ queryKey: ["novaut-runs"] });
  };
  const onTriggerError = (what: string) => (mutationError: unknown) => {
    toast.error(
      mutationError instanceof ApiError
        ? `${what} not queued: ${mutationError.message}`
        : `${what} not queued`,
    );
  };

  const plan = useMutation({
    mutationFn: () => api.novautPlan(),
    onSuccess: afterTrigger("Dry run"),
    onError: onTriggerError("Dry run"),
  });
  const writeFile = useMutation({
    mutationFn: () => api.novautWriteFile(),
    onSuccess: afterTrigger("File run"),
    onError: onTriggerError("File run"),
  });

  const runs = data?.runs ?? [];
  const total = data?.total ?? 0;
  const first = total === 0 ? 0 : offset + 1;
  const last = offset + runs.length;
  const cannotWrite = fileRunRefusal !== null;

  // Runs expire, so the current page can empty; fall back to the last page.
  useEffect(() => {
    if (data && offset > 0 && offset >= total) {
      setOffset(Math.max(0, Math.floor((total - 1) / pageSize) * pageSize));
    }
  }, [data, offset, total, pageSize]);

  return (
    <Card>
      <CardHeader className="space-y-4">
        <div className="flex flex-row items-start justify-between gap-4">
          <CardTitle>Novaut runs</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={isFetching ? "size-4 animate-spin" : "size-4"} />
            Refresh
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Button
            type="button"
            size="sm"
            onClick={() => plan.mutate()}
            disabled={plan.isPending}
          >
            {plan.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <FileText className="size-4" />
            )}
            Dry run
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => writeFile.mutate()}
            disabled={cannotWrite || writeFile.isPending}
            title={fileRunRefusal ?? undefined}
          >
            {writeFile.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}
            Write File
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        )}
        {isError && (
          <p role="alert" className="text-sm text-destructive">
            Could not load the runs: {error instanceof Error ? error.message : "unknown error"}
          </p>
        )}
        {!isLoading && !isError && total === 0 && (
          <p className="text-sm text-muted-foreground">No run yet.</p>
        )}
        {runs.length > 0 && (
          <>
            <p className="text-xs text-muted-foreground">
              Showing {first}–{last} of {total}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full caption-bottom text-sm">
                <caption className="sr-only">Novaut runs, newest first</caption>
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th scope="col" className="pb-2 pr-4 font-medium">Run</th>
                    <th scope="col" className="pb-2 pr-4 font-medium">State</th>
                    <th scope="col" className="pb-2 pr-4 font-medium">Started</th>
                    <th scope="col" className="pb-2 pr-4 font-medium">Duration</th>
                    <th scope="col" className="pb-2 pr-4 font-medium">File</th>
                    <th scope="col" className="pb-2 font-medium">Logs</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <RunRow key={run.id} run={run} />
                  ))}
                </tbody>
              </table>
            </div>
            {total > pageSize && (
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - pageSize))}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={last >= total}
                  onClick={() => setOffset(offset + pageSize)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RunRow({ run }: { run: NovautRun }) {
  const [expanded, setExpanded] = useState(false);
  const detailId = useId();
  const state = stateOf(run);
  const StateIcon = state.icon;
  const runCounters = counters(run);
  const runNotes = notes(run);

  // Ignores the click that ends a text selection.
  const toggle = () => {
    if (window.getSelection()?.isCollapsed === false) return;
    setExpanded((open) => !open);
  };

  return (
    <>
      <tr
        className="cursor-pointer border-b border-border align-top last:border-0 hover:bg-muted/50"
        onClick={() => toggle()}
      >
        <td className="py-2 pr-4 font-medium">
          {/* The row toggles on click; this button is the keyboard and
              screen-reader control, styled as the run name. */}
          <button
            type="button"
            className="rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-expanded={expanded}
            aria-controls={detailId}
            onClick={(event) => {
              event.stopPropagation();
              toggle();
            }}
          >
            {runLabel(run)}
            <span className="sr-only"> ({expanded ? "hide" : "show"} the details)</span>
          </button>
        </td>
        <td className="py-2 pr-4">
          <Badge variant={state.variant} className="gap-1 whitespace-nowrap">
            <StateIcon
              aria-hidden="true"
              className={state.spin ? "size-3 animate-spin" : "size-3"}
            />
            {state.label}
          </Badge>
        </td>
        <td className="whitespace-nowrap py-2 pr-4 tabular-nums">
          {formatTimestamp(run.createdAt)}
        </td>
        <td className="whitespace-nowrap py-2 pr-4 tabular-nums">
          {formatDuration(run.processedAt, run.finishedAt)}
        </td>
        <td className="py-2 pr-4">
          <FileCell run={run} />
        </td>
        <td className="py-2">
          {run.returnValue?.logKey ? (
            <DownloadButton
              run={run}
              what="the log"
              fileName={`novaut-${run.kind}-log-${stamp(run)}.csv`}
              fetchBlob={() => api.novautRunLog(run.id)}
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr data-run-detail className="border-b border-border last:border-0">
          <td colSpan={COLUMN_COUNT} className="pb-3">
            <div id={detailId} className="space-y-3">
              <div className="rounded-md bg-muted p-3 text-xs leading-relaxed">
                {runCounters.length > 0 && (
                  <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 sm:grid-cols-[1fr_auto_1fr_auto]">
                    {runCounters.map(([label, value]) => (
                      <Fragment key={label}>
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="text-right font-mono tabular-nums text-foreground">
                          {NUMBER.format(value)}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                )}
                {runNotes.length > 0 && (
                  <div className={runCounters.length > 0 ? "mt-2 space-y-1" : "space-y-1"}>
                    {runNotes.map((note) => (
                      <p
                        key={note.text}
                        className={note.alarming ? "text-destructive" : "text-muted-foreground"}
                      >
                        {note.text}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <p className="font-mono text-xs break-all text-muted-foreground">{run.id}</p>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// The file a file run wrote; the bucket keeps it as long as the run is listed.
function FileCell({ run }: { run: NovautRun }) {
  const name = fileNameOf(run);
  if (name === null) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <DownloadButton
      run={run}
      what="the file"
      fileName={name}
      fetchBlob={() => api.novautFileCsv(name)}
    />
  );
}

function DownloadButton({
  run,
  what,
  fileName,
  fetchBlob,
}: {
  run: NovautRun;
  what: "the file" | "the log";
  fileName: string;
  fetchBlob: () => Promise<Blob>;
}) {
  const download = useMutation({
    mutationFn: fetchBlob,
    onSuccess: (blob) => downloadBlob(blob, fileName),
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? `Could not download ${what}: ${error.message}`
          : `Could not download ${what}`,
      );
    },
  });

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="gap-1.5 whitespace-nowrap"
      disabled={download.isPending}
      onClick={(event) => {
        event.stopPropagation();
        download.mutate();
      }}
    >
      {download.isPending ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Download className="size-3.5" />
      )}
      {/* Kept out of the accessible name, which the sr-only text gives. */}
      <span aria-hidden="true">CSV</span>
      <span className="sr-only">
        Download {what}: {runLabel(run)}, {formatTimestamp(run.createdAt)}
      </span>
    </Button>
  );
}

function stateOf(run: NovautRun): StateStyle {
  const result = run.returnValue;
  if (run.kind === "file" && run.state === "failed") return NO_FILE_FAILED;
  if (run.state === "completed" && result !== null) {
    if ("refused" in result) {
      if (result.fileName === null) return NO_FILE;
    } else if (result.stoppedByShutdown) {
      return STOPPED;
    }
  }
  return STATES[run.state] ?? { label: run.state, icon: Clock, variant: "outline" };
}

function fileNameOf(run: NovautRun): string | null {
  const result = run.returnValue;
  return result !== null && "refused" in result ? result.fileName : null;
}

// The same counters in the same order every time, zeros included. The held
// are one total: their reasons are in the log.
function counters(run: NovautRun): ReadonlyArray<readonly [string, number]> {
  const result = run.returnValue;
  if (result === null) return [];
  const held = Object.values(result.held).reduce((sum, count) => sum + count, 0);
  const common: ReadonlyArray<readonly [string, number]> = [
    ["Candidates", result.candidates],
    ["Rows", result.rows],
    ["With pictures", result.withPictures],
    ["EAN left out", result.eanLeftOut],
    ["Held", held],
  ];
  return "refused" in result
    ? common
    : [["Priced products", result.productsWithOffers], ...common];
}

// What the counters do not say: the file, or why there is none.
function notes(run: NovautRun): ReadonlyArray<{ text: string; alarming?: boolean }> {
  if (run.failedReason) return [{ text: `Failed: ${run.failedReason}`, alarming: true }];
  const result = run.returnValue;
  if (result === null) return [{ text: "No result yet." }];
  return "refused" in result ? fileNotes(result) : planNotes(result);
}

function planNotes(result: NovautPlanResult): Array<{ text: string; alarming?: boolean }> {
  const notes: Array<{ text: string; alarming?: boolean }> = [];
  if (result.tried.length > 0) {
    notes.push({ text: `Trial values taken for undecided rules: ${result.tried.join(", ")}` });
  }
  if (result.stoppedByShutdown) {
    notes.push({
      text: "Stopped by a restart: the counters and the log cover the products walked until then",
      alarming: true,
    });
  }
  return notes;
}

function fileNotes(result: NovautFileResult): Array<{ text: string; alarming?: boolean }> {
  if (result.refused !== null) {
    return [{ text: `No file: refused, ${result.refused}`, alarming: true }];
  }
  if (result.stoppedByShutdown) {
    return [
      {
        text: `No file: stopped by a restart after ${NUMBER.format(result.candidates)} products`,
        alarming: true,
      },
    ];
  }
  if (result.fileName === null) return [{ text: "No file: no rows", alarming: true }];
  return [{ text: `File ${result.fileName}` }];
}

function stamp(run: NovautRun): string {
  return run.createdAt ? new Date(run.createdAt).toISOString().slice(0, 10) : run.id;
}
